import {
  ClerkProvider,
  Show,
  SignInButton,
  UserButton,
  useAuth,
} from "@clerk/react";
import { StrictMode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import "./styles.css";

interface Artifact {
  id: string;
  slug: string;
  title: string;
  currentRevisionId: string | null;
  currentRevisionStatus?: "processing" | "ready" | "failed" | null;
  currentRevisionSequence?: number | null;
  openAnnotationCount?: number;
  updatedAt: string;
  previewUrl: string | null;
}

interface Annotation {
  id: string;
  revisionId: string;
  status: "open" | "resolved" | "dismissed";
  comment: string;
  createdAt: string;
}

interface ArtifactRevision {
  id: string;
  sequence: number;
  status: "processing" | "ready" | "failed";
  createdAt: string;
  previewUrl: string | null;
}

interface ArtifactDetail {
  artifact: Artifact;
  revisions: ArtifactRevision[];
}

type TokenProvider = () => Promise<string | null>;

const clerkKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;
const isDevelopment = import.meta.env.DEV;

function Root(): React.ReactNode {
  if (!clerkKey) {
    if (isDevelopment) return <Dashboard tokenProvider={async () => null} development />;
    return (
      <SetupRequired
        title="Clerk build key missing"
        body="Set VITE_CLERK_PUBLISHABLE_KEY while building the web application. The backend Clerk secret remains a Worker secret."
      />
    );
  }
  return (
    <ClerkProvider publishableKey={clerkKey} afterSignOutUrl="/">
      <Show when="signed-out">
        <Landing />
      </Show>
      <Show when="signed-in">
        <AuthenticatedDashboard />
      </Show>
    </ClerkProvider>
  );
}

function AuthenticatedDashboard(): React.ReactNode {
  const { getToken } = useAuth();
  return <Dashboard tokenProvider={() => getToken()} />;
}

function Landing(): React.ReactNode {
  return (
    <main className="landing">
      <div className="brand-mark" aria-hidden="true"><BirdMark /></div>
      <p className="eyebrow">A spatial review loop for agents and people</p>
      <h1>Draw on what<br />the agent made.</h1>
      <p className="lede">
        Publish a small web artifact, open it on iPad, mark it up with Pencil,
        and hand exact revision-bound feedback back to the agent.
      </p>
      <SignInButton mode="modal">
        <button className="primary large">Open your drawing table <span>↗</span></button>
      </SignInButton>
      <div className="landing-rule" />
      <div className="three-up">
        <span>01 · Publish</span><span>02 · Mark up</span><span>03 · Revise</span>
      </div>
    </main>
  );
}

function Dashboard({ tokenProvider, development = false }: { tokenProvider: TokenProvider; development?: boolean }): React.ReactNode {
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [revisions, setRevisions] = useState<ArtifactRevision[]>([]);
  const [selectedRevisionId, setSelectedRevisionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [previewGeneration, setPreviewGeneration] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);

  const api = useMemo(() => createApi(tokenProvider, development), [tokenProvider, development]);
  const selected = artifacts.find((artifact) => artifact.id === selectedId) ?? artifacts[0] ?? null;
  const selectedRevision = revisions.find((revision) => revision.id === selectedRevisionId) ?? null;
  const visibleAnnotations = selectedRevisionId
    ? annotations.filter((annotation) => annotation.revisionId === selectedRevisionId)
    : annotations;

  const refresh = useCallback(async () => {
    try {
      const result = await api<{ artifacts: Artifact[] }>("/api/v1/artifacts");
      setArtifacts(result.artifacts);
      setSelectedId((current) => current ?? result.artifacts[0]?.id ?? null);
      setError(null);
    } catch (requestError) {
      setError(messageOf(requestError));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { void refresh(); }, [refresh]);

  useEffect(() => {
    if (!selected) {
      setAnnotations([]);
      setRevisions([]);
      setSelectedRevisionId(null);
      return;
    }
    setSelectedRevisionId(selected.currentRevisionId);
    void api<ArtifactDetail>(`/api/v1/artifacts/${selected.id}`)
      .then((result) => {
        setRevisions(result.revisions);
        setSelectedRevisionId((current) =>
          result.revisions.some((revision) => revision.id === current)
            ? current
            : result.artifact.currentRevisionId,
        );
      })
      .catch((requestError) => setError(messageOf(requestError)));
    void api<{ annotations: Annotation[] }>(`/api/v1/artifacts/${selected.id}/annotations`)
      .then((result) => setAnnotations(result.annotations))
      .catch((requestError) => setError(messageOf(requestError)));
  }, [api, previewGeneration, selected?.id]);

  useEffect(() => {
    if (!selected) return;
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const socket = new WebSocket(`${protocol}//${window.location.host}/api/v1/artifacts/${selected.id}/events`);
    socket.onmessage = (event) => {
      if (event.data === "pong") return;
      const message = JSON.parse(String(event.data)) as { type?: string };
      if (message.type === "revision.ready") {
        setPreviewGeneration((value) => value + 1);
        void refresh();
      }
      if (message.type?.startsWith("annotation.")) {
        void api<{ annotations: Annotation[] }>(`/api/v1/artifacts/${selected.id}/annotations`)
          .then((result) => setAnnotations(result.annotations));
      }
    };
    return () => socket.close();
  }, [api, refresh, selected?.id]);

  useEffect(() => {
    const claim = new URLSearchParams(window.location.search).get("claim");
    if (!claim) return;
    void api<{ artifact: Artifact }>("/api/v1/claims", { method: "POST", body: JSON.stringify({ token: claim }) })
      .then(() => {
        history.replaceState(null, "", "/");
        return refresh();
      })
      .catch((requestError) => setError(messageOf(requestError)));
  }, [api, refresh]);

  async function upload(file: File): Promise<void> {
    setUploading(true);
    setError(null);
    const title = file.name.replace(/\.zip$/i, "").replaceAll(/[-_]+/g, " ");
    const form = new FormData();
    form.set("title", title || "Untitled artifact");
    form.set("file", file);
    try {
      const result = await api<{ artifactId: string }>("/api/v1/artifacts", { method: "POST", body: form });
      setSelectedId(result.artifactId);
      await refresh();
    } catch (requestError) {
      setError(messageOf(requestError));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <a className="wordmark" href="/" aria-label="Bird Drawing home"><BirdMark /> bird drawing</a>
        <div className="top-actions">
          {development && <span className="dev-pill">local identity</span>}
          <button className="primary" onClick={() => fileRef.current?.click()} disabled={uploading}>
            {uploading ? "Publishing…" : "Publish ZIP"}
          </button>
          {!development && <UserButton />}
          <input ref={fileRef} hidden type="file" accept=".zip,application/zip" onChange={(event) => {
            const file = event.currentTarget.files?.[0];
            if (file) void upload(file);
          }} />
        </div>
      </header>

      {error && <div className="error-banner" role="alert">{error}<button onClick={() => setError(null)}>×</button></div>}

      <div className="workspace">
        <aside className="library">
          <div className="section-label"><span>Artifacts</span><span>{artifacts.length.toString().padStart(2, "0")}</span></div>
          {loading ? <p className="muted">Opening the table…</p> : artifacts.length === 0 ? (
            <button className="empty-card" onClick={() => fileRef.current?.click()}>
              <span className="empty-glyph">＋</span>
              <strong>Publish your first artifact</strong>
              <small>A ZIP with index.html at its root</small>
            </button>
          ) : (
            <nav className="artifact-list">
              {artifacts.map((artifact) => (
                <button
                  key={artifact.id}
                  className={artifact.id === selected?.id ? "artifact-row selected" : "artifact-row"}
                  onClick={() => setSelectedId(artifact.id)}
                >
                  <span className={`status-dot ${artifact.currentRevisionStatus ?? "processing"}`} />
                  <span className="artifact-copy">
                    <strong>{artifact.title}</strong>
                    <small>r{artifact.currentRevisionSequence ?? "–"} · {relativeDate(artifact.updatedAt)}</small>
                  </span>
                  {(artifact.openAnnotationCount ?? 0) > 0 && <span className="annotation-count">{artifact.openAnnotationCount}</span>}
                </button>
              ))}
            </nav>
          )}
        </aside>

        <main className="canvas-column">
          {selected ? (
            <>
              <div className="artifact-header">
                <div>
                  <p className="eyebrow">{selectedRevision ? `Version ${selectedRevision.sequence}` : "Current version"}</p>
                  <h2>{selected.title}</h2>
                </div>
                <div className="artifact-links">
                  {revisions.length > 0 && (
                    <select
                      className="version-select"
                      aria-label="Artifact version"
                      value={selectedRevisionId ?? ""}
                      onChange={(event) => setSelectedRevisionId(event.currentTarget.value)}
                    >
                      {revisions.map((revision) => (
                        <option key={revision.id} value={revision.id} disabled={revision.status !== "ready"}>
                          Version {revision.sequence}{revision.id === selected.currentRevisionId ? " — Current" : ""}{revision.status !== "ready" ? ` — ${revision.status}` : ""}
                        </option>
                      ))}
                    </select>
                  )}
                  {selectedRevision?.id === selected.currentRevisionId && <span className="version-current">Current</span>}
                  <span className={`state ${selectedRevision?.status ?? selected.currentRevisionStatus ?? "processing"}`}>
                    {selectedRevision?.status ?? selected.currentRevisionStatus ?? "processing"}
                  </span>
                  {(selectedRevision?.previewUrl ?? selected.previewUrl) && (
                    <a href={selectedRevision?.previewUrl ?? selected.previewUrl ?? "#"} target="_blank" rel="noreferrer">Open ↗</a>
                  )}
                </div>
              </div>
              <div className="preview-frame">
                {(selectedRevision?.previewUrl ?? selected.previewUrl) && (selectedRevision?.status ?? selected.currentRevisionStatus) !== "processing" ? (
                  <iframe key={`${selectedRevisionId}-${previewGeneration}`} src={selectedRevision?.previewUrl ?? selected.previewUrl ?? undefined} title={`${selected.title}, version ${selectedRevision?.sequence ?? selected.currentRevisionSequence ?? "current"}`} sandbox="allow-scripts allow-forms allow-modals allow-downloads allow-popups" />
                ) : (
                  <div className="processing-state"><span className="spinner" /><strong>Preparing the revision</strong><small>Validation and extraction are running in Cloudflare.</small></div>
                )}
              </div>
            </>
          ) : (
            <div className="no-selection"><BirdMark /><h2>The table is clear.</h2><p>Publish a ZIP to put something here.</p></div>
          )}
        </main>

        <aside className="notes-panel">
          <div className="section-label">
            <span>Markup{selectedRevision ? ` · V${selectedRevision.sequence}` : ""}</span>
            <span>{visibleAnnotations.filter((note) => note.status === "open").length.toString().padStart(2, "0")}</span>
          </div>
          {selected && visibleAnnotations.length === 0 ? (
            <div className="notes-empty"><span>⌁</span><p>Open this artifact on iPad and draw directly over it.</p></div>
          ) : (
            <div className="notes-list">
              {visibleAnnotations.map((note, index) => (
                <article className={`note ${note.status}`} key={note.id}>
                  <span className="note-number">{String(index + 1).padStart(2, "0")}</span>
                  <div><p>{note.comment || "Visual markup"}</p><small>{note.status} · exact version</small></div>
                </article>
              ))}
            </div>
          )}
          <div className="ipad-callout"><span>Pencil</span><p>Native iPad review preserves strokes, screenshot, viewport, and revision.</p></div>
        </aside>
      </div>
    </div>
  );
}

function createApi(tokenProvider: TokenProvider, development: boolean) {
  return async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = await tokenProvider();
    const headers = new Headers(init.headers);
    if (token) headers.set("Authorization", `Bearer ${token}`);
    if (development) headers.set("X-Bird-Drawing-User", "dev_web");
    if (typeof init.body === "string") headers.set("Content-Type", "application/json");
    const response = await fetch(path, { ...init, headers });
    const body = await response.json().catch(() => ({})) as { error?: { message?: string } };
    if (!response.ok) throw new Error(body.error?.message ?? `Request failed (${response.status})`);
    return body as T;
  };
}

function BirdMark(): React.ReactNode {
  return <svg viewBox="0 0 44 44" aria-hidden="true"><path d="M7 28c7-1 12-5 15-12 2 5 6 8 13 9-4 1-7 3-9 7-3-2-6-3-10-2-3 1-6 1-9-2Z"/><circle cx="26" cy="17" r="1.5"/></svg>;
}

function SetupRequired({ title, body }: { title: string; body: string }): React.ReactNode {
  return <main className="setup"><BirdMark /><p className="eyebrow">Configuration required</p><h1>{title}</h1><p>{body}</p></main>;
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : "Something went wrong";
}

function relativeDate(iso: string): string {
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return "now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

createRoot(document.getElementById("root")!).render(<StrictMode><Root /></StrictMode>);
